import { TFunction } from 'i18next';
import React, { useMemo } from 'react';

import { ResourceTypeDto, TaskStatusDto } from '@/api/generated';
import { useGetResourceTasks } from '@/api/useGetResourceTasks.util.ts';
import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { TimelineMessagesConfirmMessagesReadButton } from '@/components/timelineMessages/comp/confirmMessagesReadButton/TimelineMessagesConfirmMessagesReadButton.comp.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix';
import { CustomerDetailsAccordion } from '@/views/customerDetails/components/CustomerDetailsAccordion.comp.tsx';
import { ProcessDetailsTasksTableRow } from '@/views/processesDetails/components/tasks/comp/ProcessDetailsTasksTableRow.comp.tsx';
import { TasksListTable } from '@/views/tasks/list/components/table/TasksListTable.comp.tsx';

type Props = {
  processId: string;
};

const resourceTypeOptionsFn = (t: TFunction): SelectSearchOptionModel<ResourceTypeDto>[] => [
  {
    label: t(`resourceTypeOptions.${ResourceTypeDto.Process}`),
    value: ResourceTypeDto.Process,
  },
  {
    label: t(`resourceTypeOptions.${ResourceTypeDto.ProcessOffer}`),
    value: ResourceTypeDto.ProcessOffer,
  },
];

const defaultStatusesFn = (t: TFunction): SelectSearchOptionModel<TaskStatusDto>[] => [
  {
    label: t(`taskStatusOptions.${TaskStatusDto.New}`),
    value: TaskStatusDto.New,
  },
  {
    label: t(`taskStatusOptions.${TaskStatusDto.InProgress}`),
    value: TaskStatusDto.InProgress,
  },
  {
    label: t(`taskStatusOptions.${TaskStatusDto.DoneNotConfirmed}`),
    value: TaskStatusDto.DoneNotConfirmed,
  },
  {
    label: t(`taskStatusOptions.${TaskStatusDto.Postponed}`),
    value: TaskStatusDto.Postponed,
  },
];

export const ProcessDetailsTasks: React.FC<Props> = props => {
  const { processId } = props;
  const { getResourceTasks } = useGetResourceTasks({ resourceId: processId });
  const { t } = useTranslationWithPrefix('views.processesDetails.tasks');
  const { t: tasksT } = useTranslationWithPrefix('views.tasks.list.table.head');

  const resourceTypeOptions: SelectSearchOptionModel<ResourceTypeDto>[] = useMemo(
    () => resourceTypeOptionsFn(tasksT),
    [tasksT],
  );
  const defaultStatuses: SelectSearchOptionModel<TaskStatusDto>[] = useMemo(
    () => defaultStatusesFn(tasksT),
    [tasksT],
  );

  return (
    <CustomerDetailsAccordion expanded title={t('title')}>
      <TasksListTable
        additionalButtons={[
          {
            button: TimelineMessagesConfirmMessagesReadButton,
            name: 'confirmMessagesRead',
            params: { resource: { resourceId: processId, type: ResourceTypeDto.Process } },
          },
        ]}
        customRow={ProcessDetailsTasksTableRow}
        initialParams={{
          contextResourceId: processId,
          contextResourceTypes: resourceTypeOptions,
          taskStatuses: defaultStatuses,
        }}
        request={getResourceTasks}
        size="small"
        tableKey={`process_tasks-${processId}`}
        typeDefined
      />
    </CustomerDetailsAccordion>
  );
};
